// Turns an audience configuration's raw filter conditions into a readable,
// business-speak narrative for the Makeup screen. Pure (no React) so it can be
// unit-tested and reused by any view. We deliberately never surface raw column
// or table names — transaction fields are mapped to marketer language, and
// anything unmapped (notably user-attribute ids, whose human labels live in
// ClickHouse, not in state) falls back to a humanized form.
//
// A transaction audience has TWO levels, and the narrative keeps them distinct:
//   1. Transaction filter (`TransactionDefinition`) — what makes a single
//      purchase match (field conditions).
//   2. User behavior (`UserTransactionBehavior`) — which people to target, by
//      the purchases they've made (built from level-1 patterns).
// At BOTH levels the must/should/must_not buckets mean the same thing, so we
// translate by EFFECTIVE LOGIC rather than the raw key:
//   - must     → required (AND): all must be present.
//   - should   → "at least one of" (OR), but it is a HARD requirement, not a
//                preference; a lone `should` is identical to a `must`, so it
//                folds into the required group. Only a 2+ `should` reads as a
//                distinct "any of" group.
//   - must_not → excluded (a negated OR / NOR): none may be present; matching
//                ANY one excludes the record/user.

import type {
    AttributeGroup,
    BooleanFieldValue,
    FieldValue,
    TransactionDefinition,
    UserAttributesDefinition,
    UserTransactionBehavior,
} from '@/lib/domain'

// A leaf predicate at either level: a transaction `FieldValue` (no negation) or a
// user-attribute `BooleanFieldValue` (which may carry `negate`). Both expose
// `field` + `values`, so the bucketing/labelling machinery is shared.
type LeafCondition = FieldValue | BooleanFieldValue

// A must/should/must_not group of leaf predicates — a `TransactionDefinition`
// (transaction side) or an `AttributeGroup` (user-attribute side).
type ConditionGroup = TransactionDefinition | AttributeGroup

// Business-friendly label per transaction dataset field family. Covers the
// user_demographics_intent / cpg_transactions transaction schemas; unknown fields humanize.
const TXN_FIELD_LABELS: Record<string, string> = {
    brand_name: 'Brand',
    store_name: 'Store',
    channel: 'Channel',
    card_type: 'Card type',
    mcc_label: 'Category',
    mcc_general_category: 'Category',
    iab_tier_1: 'Category',
    monetary_score_brand: 'Spend',
    monetary_score_brand_mcc: 'Spend',
    monetary_score_brand_iab: 'Spend',
    frequency_score_brand: 'Purchase frequency',
    frequency_score_brand_mcc: 'Purchase frequency',
    frequency_score_brand_iab: 'Purchase frequency',
    recency_score_brand: 'Recency',
    recency_score_brand_mcc: 'Recency',
    recency_score_brand_iab: 'Recency',
    state: 'Location',
    msa_name: 'Location',
    metro_name: 'Location',
    primary_ruca: 'Area type',
    brand_type: 'Brand type',
    store_type: 'Store type',
}

const titleCase = (s: string): string =>
    s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())

const humanize = (s: string): string => titleCase(s.replaceAll('_', ' '))

// Known coded values mapped to plain words.
const VALUE_LABELS: Record<string, string> = {
    high: 'High',
    medium: 'Medium',
    low: 'Low',
    online: 'Online',
    'b&m': 'In-store',
    debit: 'Debit',
    credit: 'Credit',
    unknown: 'Unknown',
    true: 'Yes',
    false: 'No',
}

const formatValue = (v: string): string => {
    const key = v.trim().toLowerCase()
    if (VALUE_LABELS[key]) return VALUE_LABELS[key]
    // ALL-CAPS multiword values (brand / store names) → Title Case.
    if (v === v.toUpperCase() && /[a-z]/i.test(v)) return titleCase(v)
    return v
}

const txnLabel = (field: string): string =>
    TXN_FIELD_LABELS[field] ?? humanize(field)

// --- Effective-logic bucketing ----------------------------------------------

// How a group of conditions/patterns combines, after collapsing the lone-should
// case (see file header).
export type EffectiveKind = 'required' | 'anyOf' | 'excluded'

// Bucket a must/should/must_not triple into effective groups. A lone `should`
// folds into `required` (it is logically identical to a `must`); a 2+ `should`
// becomes its own `anyOf`; `must_not` is `excluded`. Empty groups are dropped,
// and the result is always ordered required → anyOf → excluded.
const effectiveGroups = <T>(
    must: T[],
    should: T[],
    mustNot: T[]
): { kind: EffectiveKind; items: T[] }[] => {
    const required = should.length === 1 ? [...must, ...should] : must
    const anyOf = should.length >= 2 ? should : []
    const groups: { kind: EffectiveKind; items: T[] }[] = []
    if (required.length) groups.push({ kind: 'required', items: required })
    if (anyOf.length) groups.push({ kind: 'anyOf', items: anyOf })
    if (mustNot.length) groups.push({ kind: 'excluded', items: mustNot })
    return groups
}

// --- Level 1: transaction filter (field conditions) -------------------------

export interface NarrativeCondition {
    label: string
    values: string[]
    // True for a negated attribute predicate (`BooleanFieldValue.negate`). The
    // view renders it as "not <values>" even inside a should/required group; XOR
    // it with the bucket's own negation (a must_not group). Transaction field
    // conditions never set this.
    negate?: boolean
}

export interface ConditionBucket {
    kind: EffectiveKind
    conditions: NarrativeCondition[]
}

const conditionOf = (
    fv: LeafCondition,
    labelFor: (field: string) => string
): NarrativeCondition => ({
    label: labelFor(fv.field),
    values: (fv.values ?? []).map(formatValue),
    negate: 'negate' in fv && fv.negate ? true : undefined,
})

// A condition group's leaf conditions, bucketed by effective logic. Works for a
// transaction pattern (`TransactionDefinition`, txn field labels) and for one
// user-attribute group (`AttributeGroup`, humanized attribute ids).
export const definitionNarrative = (
    definition: ConditionGroup | undefined,
    labelFor: (field: string) => string = humanize
): ConditionBucket[] => {
    if (!definition) return []
    const valid = (fvs: LeafCondition[] | undefined): LeafCondition[] =>
        (fvs ?? []).filter((fv) => fv?.field)
    return effectiveGroups(
        valid(definition.must),
        valid(definition.should),
        valid(definition.must_not)
    ).map((g) => ({
        kind: g.kind,
        conditions: g.items.map((fv) => conditionOf(fv, labelFor)),
    }))
}

// A user-attribute definition is two-level too — must/should/must_not of
// `AttributeGroup`, each group a must/should/must_not of `BooleanFieldValue`. So
// it narrates exactly like a transaction behavior's `patternBuckets`: bucket the
// groups by effective logic, narrating each group's conditions as one "pattern".
// The common case (a single group in `must`) yields one required bucket with one
// pattern, which the view renders inline.
export const attributeNarrative = (
    definition: UserAttributesDefinition | undefined,
    labelFor: (field: string) => string = humanize
): PatternBucket[] => {
    if (!definition) return []
    const groups = (gs: AttributeGroup[] | undefined): PatternNarrative[] =>
        (gs ?? [])
            .map((g) => ({ buckets: definitionNarrative(g, labelFor) }))
            .filter((p) => p.buckets.length > 0)
    return effectiveGroups(
        groups(definition.must),
        groups(definition.should),
        groups(definition.must_not)
    ).map((g) => ({ kind: g.kind, patterns: g.items }))
}

// --- Level 2: user behavior (which transactions a user made) ----------------

// One transaction pattern (a `TransactionDefinition`), narrated.
export interface PatternNarrative {
    buckets: ConditionBucket[]
}

// Patterns bucketed by effective logic at the user level.
export interface PatternBucket {
    kind: EffectiveKind
    patterns: PatternNarrative[]
}

// One transaction behavior: its plain-English description plus the pattern
// buckets (which purchases the user must / at-least-one-of / must-not have made).
export interface BehaviorNarrative {
    description?: string
    patternBuckets: PatternBucket[]
}

const behaviorNarrative = (
    behavior: UserTransactionBehavior
): BehaviorNarrative => {
    // Narrate each pattern, dropping any that carry no conditions so empty
    // patterns don't skew the lone-should fold or render as blank boxes.
    const patterns = (
        defs: TransactionDefinition[] | undefined
    ): PatternNarrative[] =>
        (defs ?? [])
            .map((def) => ({ buckets: definitionNarrative(def, txnLabel) }))
            .filter((p) => p.buckets.length > 0)
    const patternBuckets = effectiveGroups(
        patterns(behavior.must),
        patterns(behavior.should),
        patterns(behavior.must_not)
    ).map((g) => ({ kind: g.kind, patterns: g.items }))
    return {
        description: behavior.description?.trim() || undefined,
        patternBuckets,
    }
}

// All behaviors for a transaction (or crossed) config. Users match ANY behavior
// (OR). Behaviors with neither a description nor any patterns are dropped.
export const transactionNarrative = (
    behaviors: UserTransactionBehavior[] | undefined
): BehaviorNarrative[] =>
    (behaviors ?? [])
        .map(behaviorNarrative)
        .filter((b) => b.patternBuckets.length > 0 || b.description)

// --- Presentation labels (the views read these) -----------------------------

// User-behavior level: what the person must have done. `count` distinguishes the
// "all of" / "any of" wording when a group has more than one pattern.
export const userBucketLabel = (kind: EffectiveKind, count: number): string => {
    switch (kind) {
        case 'required':
            return count > 1 ? 'Bought all of' : 'Bought'
        case 'anyOf':
            return 'Bought any of'
        case 'excluded':
            return count > 1 ? 'Never bought any of' : 'Never bought'
    }
}

// User-attribute group level: how the attribute groups combine. Mirrors
// `userBucketLabel` but in attribute-matching language (a group is a set of
// attribute conditions, not a purchase). `count` tunes the "all of" wording.
export const attributeGroupLabel = (
    kind: EffectiveKind,
    count: number
): string => {
    switch (kind) {
        case 'required':
            return count > 1 ? 'Match all of' : 'Match'
        case 'anyOf':
            return 'Match any of'
        case 'excluded':
            return count > 1 ? 'Exclude any of' : 'Exclude'
    }
}

// Field level (inside a transaction pattern / a user-attribute definition).
// Required conditions are listed plainly (no heading); the others are flagged.
export const fieldBucketLabel = (kind: EffectiveKind): string | null => {
    switch (kind) {
        case 'required':
            return null
        case 'anyOf':
            return 'Any of'
        case 'excluded':
            return 'Excluding'
    }
}

export const BUCKET_COLOR: Record<EffectiveKind, string> = {
    required: 'teal',
    anyOf: 'blue',
    excluded: 'red',
}
